import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { apiEndpoints, handleApiError } from '../services/api';
import { createApiUrlWithPrefix, createApiUrl, getUserImageUrl } from '../config/api';
import { toast } from 'react-hot-toast';
import { X, UserPlus, Mail, Phone, Calendar, Save, Upload, Camera, FileText, User, Edit3 } from 'lucide-react';

function UserEditModal({ isOpen, onClose, user, onUserUpdated, allowedRoles = null, disableRoleEdit = false }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Define all available roles
  const allRoles = [
    { value: 0, label: 'Rider' },
    { value: 1, label: 'Owner' },
    { value: 2, label: 'Coach' },
    { value: 3, label: 'Admin' },
    { value: 4, label: 'SuperAdmin' },
    { value: 5, label: 'Temporary' }
  ];

  // Filter roles based on allowedRoles prop
  const availableRoles = allowedRoles
    ? allRoles.filter(role => allowedRoles.includes(role.value))
    : allRoles;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    birthDate: '',
    address: '',
    bloodType: '',
    level: '',
    role: 0,
    status: 0
  });
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [pjDocumentFile, setPjDocumentFile] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        birthDate: user.birthDate ? user.birthDate.split('T')[0] : '',
        address: user.address || '',
        bloodType: user.bloodType || '',
        level: user.level !== null && user.level !== undefined ? user.level.toString() : '',
        role: user.role || 0,
        status: user.status || 0
      });
    }
  }, [user]);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (type === 'photo') {
      setPhotoFile(file);
    } else if (type === 'pjDocument') {
      setPjDocumentFile(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert role and status numbers to strings for proper enum handling
      const roleMap = { 0: 'Rider', 1: 'Owner', 2: 'Coach', 3: 'Admin', 4: 'SuperAdmin' };
      const statusMap = { 0: 'Active', 1: 'Deactivated' };

      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString() : null,
        address: formData.address,
        bloodType: formData.bloodType,
        level: formData.level ? parseInt(formData.level) : null, // Convert to number
        photoPath: user.photoPath || null,
        pjDocumentPath: user.pjDocumentPath || null,
        role: parseInt(formData.role), // Keep as number
        status: parseInt(formData.status) // Keep as number
      };

      // Debug logging
      console.log('Update data being sent:', {
        birthDate: formData.birthDate,
        birthDateISO: formData.birthDate ? new Date(formData.birthDate).toISOString() : null,
        birthDateType: typeof formData.birthDate,
        fullFormData: formData,
        updateData: updateData
      });

      await axios.put(createApiUrlWithPrefix(`users/${user.id}`), updateData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      // Upload photo if provided
      if (photoFile) {
        try {
          const photoFormData = new FormData();
          photoFormData.append('photo', photoFile);
          // Do NOT set Content-Type manually - let axios/browser set it with boundary
          await axios.post(createApiUrlWithPrefix(`users/upload-photo/${user.id}`), photoFormData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
              // Content-Type will be set automatically by axios with boundary
            }
          });
          console.log('✅ Photo uploaded successfully');
        } catch (photoError) {
          console.error('❌ Photo upload error:', photoError);
          console.error('❌ Photo upload error response:', photoError.response);
          console.error('❌ Photo upload error data:', photoError.response?.data);

          // Safely extract error message with proper null checks
          let errorMessage = 'Failed to upload photo';
          if (photoError?.response?.data) {
            const errorData = photoError.response.data;
            errorMessage = errorData?.message || errorData?.details || errorData?.error || errorMessage;
          } else if (photoError?.message) {
            errorMessage = photoError.message;
          }

          throw new Error(`Photo upload failed: ${errorMessage}`);
        }
      }

      // Upload PJ document if provided
      if (pjDocumentFile) {
        try {
          const documentFormData = new FormData();
          documentFormData.append('document', pjDocumentFile);
          // Do NOT set Content-Type manually - let axios/browser set it with boundary
          await axios.post(createApiUrlWithPrefix(`users/upload-pj-document/${user.id}`), documentFormData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
              // Content-Type will be set automatically by axios with boundary
            }
          });
          console.log('✅ Document uploaded successfully');
        } catch (docError) {
          console.error('❌ Document upload error:', docError);
          const errorMessage = docError.response?.data?.message ||
            docError.response?.data?.details ||
            docError.message ||
            'Failed to upload document';
          throw new Error(`Document upload failed: ${errorMessage}`);
        }
      }

      toast.success('User updated successfully!');
      onUserUpdated();
    } catch (error) {
      console.error('Failed to update user:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.details ||
        error.message ||
        'Failed to update user. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 3: return 'Personnel';
      case 2: return 'Coach';
      case 0: return 'Rider';
      case 1: return 'Owner';
      case 4: return 'SuperAdmin';
      default: return 'User';
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 0: return 'Active';
      case 1: return 'Deactivated';
      default: return 'Active';
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                <Edit3 className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  Edit {getRoleDisplayName(user.role)}
                </h2>
                <p className="text-green-100 text-sm">{t('common.updateUserInfo')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-green-200 transition-colors p-2 hover:bg-white hover:bg-opacity-10 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information Section */}
          <div className="bg-gray-50 rounded-xl p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 text-green-600 mr-2" />
              {t('common.personalInformation')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.firstName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder={t('users.firstName')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.lastName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder={t('users.lastName')}
                />
              </div>
            </div>

            {(user.role === 3 || user.role === 4) && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.emailAddress')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                    placeholder={t('users.email')}
                  />
                </div>
              </div>
            )}

            {(user.role === 0 || user.role === 1 || user.role === 2) && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.emailAddress')} <span className="text-gray-400">({t('common.optional')})</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter email address (optional)"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.phoneNumber')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                    placeholder={t('users.phoneNumber')}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.birthDate')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="date"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.address')}
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder={t('users.address')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.bloodType')}
                </label>
                <select
                  name="bloodType"
                  value={formData.bloodType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Select blood type</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>

              {/* Rider Level Field - Show for Riders, Coaches and Temporary users */}
              {(formData.role === 0 || formData.role === 2 || formData.role === 5) && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('common.riderLevel')}
                  </label>
                  <select
                    name="level"
                    value={formData.level}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select rider level</option>
                    <option value="0">Beginner</option>
                    <option value="1">Intermediate</option>
                    <option value="2">Professional</option>
                  </select>
                </div>
              )}
            </div>

            {!disableRoleEdit && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  {availableRoles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.status')}
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              >
                <option value={0}>{t('common.active')}</option>
                <option value={1}>{t('common.deactivated')}</option>
              </select>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="bg-gray-50 rounded-xl p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Upload className="w-5 h-5 text-green-600 mr-2" />
              {t('common.documentsPhotos')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Camera className="w-4 h-4 inline mr-1" />
                  {t('common.profilePhoto')}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-green-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'photo')}
                    className="hidden"
                    id="photo-upload-edit"
                  />
                  <label htmlFor="photo-upload-edit" className="cursor-pointer flex flex-col items-center">
                    <Camera className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Click to upload photo</span>
                    <span className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB</span>
                  </label>
                  {photoFile && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700 flex items-center">
                        <Camera className="w-4 h-4 mr-1" />
                        Selected: {photoFile.name}
                      </p>
                    </div>
                  )}
                  {getUserImageUrl(user) && !photoFile && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700 flex items-center mb-2">
                        <Camera className="w-4 h-4 mr-1" />
                        Current: {(user.photoPath || user.imagePath || '').split('/').pop()}
                      </p>
                      <div className="flex items-center space-x-2">
                        <img
                          src={getUserImageUrl(user)}
                          alt="Current photo"
                          className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div className="hidden text-xs text-gray-500">
                          Image not available
                        </div>
                        <a
                          href={getUserImageUrl(user)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          {t('common.viewFullSize')}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="w-4 h-4 inline mr-1" />
                  {t('common.pjDocument')}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-green-400 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange(e, 'pjDocument')}
                    className="hidden"
                    id="pj-upload-edit"
                  />
                  <label htmlFor="pj-upload-edit" className="cursor-pointer flex flex-col items-center">
                    <FileText className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Click to upload document</span>
                    <span className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX up to 10MB</span>
                  </label>
                  {pjDocumentFile && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700 flex items-center">
                        <FileText className="w-4 h-4 mr-1" />
                        {t('common.selected')}: {pjDocumentFile.name}
                      </p>
                    </div>
                  )}
                  {user.pjDocumentPath && !pjDocumentFile && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700 flex items-center mb-2">
                        <FileText className="w-4 h-4 mr-1" />
                        {t('common.current')}: {user.pjDocumentPath.split('/').pop()}
                      </p>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                          <FileText className="w-8 h-8 text-gray-400" />
                        </div>
                        <div className="flex flex-col">
                          <a
                            href={createApiUrl(user.pjDocumentPath)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            {t('common.viewDocument')}
                          </a>
                          <span className="text-xs text-gray-500">
                            {user.pjDocumentPath.split('.').pop().toUpperCase()} {t('common.document')}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* User Information Section */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-5">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <UserPlus className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  User Information
                </h3>
                <div className="bg-white p-3 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700">
                    <strong>Role:</strong> <code className="bg-green-100 px-1 rounded">{getRoleDisplayName(user.role)}</code><br />
                    <strong>Status:</strong> <code className="bg-green-100 px-1 rounded">{getStatusDisplayName(user.status)}</code><br />
                    <strong>Created:</strong> <code className="bg-green-100 px-1 rounded">{new Date(user.createdAt).toLocaleDateString()}</code>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium shadow-lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  {t('common.updateUser')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserEditModal;
